import { Controller } from "stimulus";
import { placeCapacityPopover } from "../daycare_calendar/capacity";

const MOBILE_QUERY = "(max-width: 639.98px)";
const SWIPE_CLOSE = 80;

export default class extends Controller {
  connect() {
    this.button = null;
    this.popover = null;
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.boundClick = (event) => this.handleDocumentClick(event);
    this.boundKeydown = (event) => this.handleKeydown(event);
    this.boundResize = () => this.reposition();
    this.boundBreakpoint = () => this.close(false);
    this.boundTouchStart = (event) => this.touchStart(event);
    this.boundTouchMove = (event) => this.touchMove(event);
    this.boundTouchEnd = () => this.touchEnd();

    document.addEventListener("click", this.boundClick);
    document.addEventListener("keydown", this.boundKeydown);
    window.addEventListener("resize", this.boundResize);
    if (this.mediaQuery.addEventListener) this.mediaQuery.addEventListener("change", this.boundBreakpoint);
  }

  disconnect() {
    this.close(false);
    document.removeEventListener("click", this.boundClick);
    document.removeEventListener("keydown", this.boundKeydown);
    window.removeEventListener("resize", this.boundResize);
    if (this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener("change", this.boundBreakpoint);
  }

  toggle(event) {
    const button = event.currentTarget;
    if (this.button === button) {
      this.close();
      return;
    }

    this.close(false);
    const popover = this.element.querySelector(`[data-capacity-pop="${button.dataset.capacityId}"]`);
    if (!popover) return;

    this.button = button;
    this.popover = popover;
    button.classList.add("is-open");
    button.setAttribute("aria-expanded", "true");
    popover.hidden = false;

    if (this.mediaQuery.matches) {
      this.openSheet();
    } else {
      this.reposition();
    }
    popover.focus({ preventScroll: true });
  }

  close(restoreFocus = true) {
    if (!this.popover) return;

    const { button, popover } = this;
    popover.hidden = true;
    popover.classList.remove("dc-cap-sheet");
    popover.style.transform = "";
    popover.removeEventListener("touchstart", this.boundTouchStart);
    popover.removeEventListener("touchmove", this.boundTouchMove);
    popover.removeEventListener("touchend", this.boundTouchEnd);
    button.classList.remove("is-open");
    button.setAttribute("aria-expanded", "false");
    const scrim = this.scrim();
    if (scrim) scrim.hidden = true;
    document.body.classList.remove("dc-cap-sheet-open");

    this.button = null;
    this.popover = null;
    if (restoreFocus !== false && button.isConnected) button.focus({ preventScroll: true });
  }

  reposition() {
    const { button, popover } = this;
    if (!popover || popover.classList.contains("dc-cap-sheet")) return;

    popover.style.left = "0px";
    popover.style.top = "0px";
    popover.style.maxHeight = "";
    const parent = popover.offsetParent || document.body;
    const column = button.dataset.capacityAlign === "left" ? button.closest(".dc-day") : null;
    const bar = document.querySelector(".sticky-top-bar");
    const placement = placeCapacityPopover({
      place: button.dataset.capacityPlace,
      anchor: button.getBoundingClientRect(),
      column: column ? column.getBoundingClientRect() : null,
      bounds: this.element.getBoundingClientRect(),
      viewport: { top: bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0, bottom: window.innerHeight },
      width: popover.offsetWidth,
      height: popover.offsetHeight,
    });
    const base = parent.getBoundingClientRect();

    popover.style.left = `${placement.left - base.left}px`;
    popover.style.top = `${placement.top - base.top}px`;
    popover.style.maxHeight = placement.maxHeight === undefined ? "" : `${placement.maxHeight}px`;
    popover.dataset.side = placement.side;
  }

  openSheet() {
    const { popover } = this;
    popover.classList.add("dc-cap-sheet");
    popover.style.left = "";
    popover.style.top = "";
    popover.style.maxHeight = "";
    const scrim = this.scrim();
    if (scrim) scrim.hidden = false;
    document.body.classList.add("dc-cap-sheet-open");
    popover.addEventListener("touchstart", this.boundTouchStart, { passive: true });
    popover.addEventListener("touchmove", this.boundTouchMove, { passive: true });
    popover.addEventListener("touchend", this.boundTouchEnd);
  }

  touchStart(event) {
    this.dragStart = this.popover.scrollTop <= 0 ? event.touches[0].clientY : null;
    this.dragDistance = 0;
  }

  touchMove(event) {
    if (this.dragStart === null || this.dragStart === undefined) return;

    this.dragDistance = Math.max(0, event.touches[0].clientY - this.dragStart);
    this.popover.style.transform = this.dragDistance > 0 ? `translateY(${this.dragDistance}px)` : "";
  }

  touchEnd() {
    if (!this.popover) return;

    if (this.dragDistance > SWIPE_CLOSE) {
      this.close();
    } else {
      this.popover.style.transform = "";
    }
    this.dragStart = null;
    this.dragDistance = 0;
  }

  handleDocumentClick(event) {
    if (!this.popover) return;

    const target = event.target;
    if (!target || !target.isConnected) return;
    if (this.popover.contains(target) || (this.button && this.button.contains(target))) return;
    if (target.closest(".dc-cap-scrim")) return;

    this.close(false);
  }

  handleKeydown(event) {
    if (event.key !== "Escape" || !this.popover) return;

    event.preventDefault();
    this.close();
  }

  scrim() {
    return this.element.querySelector(".dc-cap-scrim");
  }
}
